import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as AlertIcon } from '../../../assets/icons/alert-icon.svg';
import { appCurrentBranding } from '../../../config/branding';
import config from '../../../config/config';
import { branding, PortalType } from '../../../config/enum';
import LanguageSelector from '../../../contexts/language-selector/LanguageSelector';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import { getCookie, setCookie } from '../../../shared-functions/Cookies';
import {
  decodeJWT,
  permissionList,
  platformPermissionList,
} from '../../../shared-functions/JWTDocodeFunction';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import {
  emailValidation,
  phoneNumberValidation,
  textAndNumberValidation,
} from '../../../shared-functions/Validation';
import authApi from '../../../utils/api/v2/auth';

/**
 * Clear session tokens from local storage.
 */
const clearSessionAndRedirect = () => {
  removeLocalStorageItem('ev-access-token');
  removeLocalStorageItem('ev-access-expires');
  removeLocalStorageItem('ev-refresh-token');
  removeLocalStorageItem('tenantId');
  removeLocalStorageItem('internalApiKey');
  removeLocalStorageItem('client-name');
  removeLocalStorageItem('userVerification');
  removeLocalStorageItem('accountVerificationStatus');
  removeLocalStorageItem('partnerName');
  removeLocalStorageItem('accountCustomId');
};

/**
 * Interface representing the login success response payload.
 */
interface ILoginSuccessResponse {
  clientName: string | null | undefined;
  accountVerificationStatus?: string | null;
  access?: {
    token?: string;
    expires?: string;
  };
  refresh?: {
    token?: string;
    expires?: string;
  };
  isEmailVerified?: boolean;
  isPhoneNumberVerified?: boolean;
  isInitialPasswordReset?: boolean;
}

/**
 * Login page function component
 */
function LoginComponent() {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { portalType, setActivePortal } = usePortal();
  const accountCustomId = getCookie('accountCustomId');
  const [searchParameters] = useSearchParams();
  const location = useLocation();

  const [credentials, setCredentials] = useState<{
    emailOrPhoneNumber: string;
    password: string;
    type: string | number;
    countryCode: string;
    accountCustomId?: string;
    platform: string;
  }>({
    emailOrPhoneNumber: '',
    password: '',
    type: 'email',
    // PARTNER ONLY
    ...(portalType === PortalType.PARTNER && {
      accountCustomId: accountCustomId || '',
    }),
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
    platform: portalType,
  });
  const [submitOpen, setSubmitOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [tenantDeactivatedPopupOpen, setTenantDeactivatedPopupOpen] = useState(false);
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });
  // Set when the login API rejects the credentials (e.g. wrong email/password),
  // so both fields can be highlighted even though only one request failed.
  const [credentialError, setCredentialError] = useState(false);

  /**
   * Input field change function
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    if (name === 'emailOrPhoneNumber' || name === 'password') {
      setCredentialError(false);
    }
    if (name === 'type') {
      setCredentials({
        ...credentials,
        [name]: changeValue,
        emailOrPhoneNumber: '',
      });
    } else {
      setCredentials({
        ...credentials,
        [name]: changeValue,
      });
    }
  };

  /**
   * Whether the partner account-ID field should show an error state.
   * @returns {boolean} true when the form has been submitted and the value is empty or fails format validation
   */
  const hasAccountIdError = () =>
    credentialError ||
    (submitOpen && !textAndNumberValidation(20, 20, credentials.accountCustomId || ''));

  /**
   * Resolves which message to show under the account-ID field, in
   * priority order: required, format invalid, then API credential error.
   * @returns {string} translated error message
   */
  const accountIdErrorMessage = () => {
    if (!credentials.accountCustomId) return t('pleaseEnterAccountId');
    if (!textAndNumberValidation(20, 20, credentials.accountCustomId)) return t('invalidAccountId');
    return t('incorrectAccountId');
  };

  /**
   * Whether email-or-phone field should show an error state.
   * @returns {boolean} true when field is empty, format invalid, or API rejected credentials
   */
  const hasEmailOrPhoneError = () => {
    if (credentialError) return true;
    if (!submitOpen) return false;
    if (!credentials.emailOrPhoneNumber) return true;
    return credentials.type === 'email'
      ? !emailValidation(credentials.emailOrPhoneNumber)
      : !phoneNumberValidation(credentials.emailOrPhoneNumber);
  };

  /**
   * Resolves which message to show under the email-or-phone field, in
   * priority order: required, format invalid, then API credential error.
   * @returns {string} translated error message
   */
  const emailOrPhoneErrorMessage = () => {
    if (!credentials.emailOrPhoneNumber) {
      return credentials.type === 'email' ? t('pleaseEnterEmail') : t('pleaseEnterPhoneNumber');
    }
    const formatInvalid =
      credentials.type === 'email'
        ? !emailValidation(credentials.emailOrPhoneNumber)
        : !phoneNumberValidation(credentials.emailOrPhoneNumber);
    if (formatInvalid) {
      return credentials.type === 'email' ? t('emailInvalid') : t('phoneInvalid');
    }
    return credentials.type === 'email'
      ? t('incorrectEmailOrPassword')
      : t('incorrectPhoneOrPassword');
  };

  /**
   * Whether password field should show an error state.
   * @returns {boolean} true when field is empty or API rejected credentials
   */
  const hasPasswordError = () => credentialError || (submitOpen && !credentials.password);

  /**
   * Resolves which message to show under the password field: required
   * first, otherwise the API credential error.
   * @returns {string} translated error message
   */
  const passwordErrorMessage = () => {
    if (!credentials.password) return t('pleaseEnterPassword');
    return credentials.type === 'email'
      ? t('incorrectEmailOrPassword')
      : t('incorrectPhoneOrPassword');
  };

  /**
   * Function to handle the login success
   * @param {ILoginSuccessResponse} response - The response object containing session tokens and user status
   */
  const loginSuccessHandler = (response: ILoginSuccessResponse) => {
    // Store the verification status in local storage
    setLocalStorageItem('accountVerificationStatus', response?.accountVerificationStatus);

    if (!response?.clientName) {
      setTenantDeactivatedPopupOpen(true);
      setDisabled(false);
      return;
    }

    // Store tokens in local storage
    if (response?.access?.token) {
      setLocalStorageItem('ev-access-token', response?.access?.token);
      setLocalStorageItem('ev-access-expires', response?.access?.expires);

      // Store tenantId from jwt payload, used for roaming (OCPI) module requests
      const decodedToken = decodeJWT(response.access.token);
      if (decodedToken?.tenantId) {
        setLocalStorageItem('tenantId', decodedToken.tenantId);
      }
      setLocalStorageItem('internalApiKey', config.internalApiKey);
    }
    if (response?.refresh?.token) {
      setLocalStorageItem('ev-refresh-token', response?.refresh?.token);
    }

    // Store access token inside cookies
    setLocalStorageItem('ev-access-token', response?.access?.token);

    setLocalStorageItem('ev-access-expires', response?.access?.expires);
    // Store refresh token inside cookies

    const userVerification = {
      isEmailVerified: response?.isEmailVerified,
      isPhoneNumberVerified: response?.isPhoneNumberVerified,
      isInitialPasswordReset: response?.isInitialPasswordReset,
    };

    // Set verification in local storage to use in email verification page
    setLocalStorageItem('userVerification', userVerification);

    setFormStatus({
      showFormErrors: false,
      buttonLoader: false,
    });

    const platformPermissions = platformPermissionList();
    if (!platformPermissions || platformPermissions.length === 0) {
      removeLocalStorageItem('ev-access-token');
      removeLocalStorageItem('ev-access-expires');
      removeLocalStorageItem('ev-refresh-token');
      removeLocalStorageItem('tenantId');
      removeLocalStorageItem('internalApiKey');
      removeLocalStorageItem('client-name');
      removeLocalStorageItem('userVerification');
      removeLocalStorageItem('accountVerificationStatus');
      removeLocalStorageItem('partnerName');
      removeLocalStorageItem('accountCustomId');

      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: 401,
            message: t('noPermissionsError'),
          },
        }),
      );
      return;
    }

    // Navigate to next page based on email and phone number verification
    if (!response?.isEmailVerified) {
      navigate('../login/email-verification');
    } else if (!response?.isPhoneNumberVerified) {
      navigate('../login/phone-number-verification');
    } else if (!response?.isInitialPasswordReset) {
      navigate('../login/reset-initial-password');
    } else if (
      response?.isEmailVerified &&
      response?.isPhoneNumberVerified &&
      response?.isInitialPasswordReset
    ) {
      removeLocalStorageItem('userVerification'); // If the user is verified remove the verification data in local storage
      if (response?.accountVerificationStatus === 'approved') {
        const permissions = permissionList();
        console.log('success', response?.accountVerificationStatus);
        // Dashboard permission
        if (permissions.includes('partnerDashboardView')) {
          if (portalType === PortalType.CHARGER) {
            navigate('/ev-admin/ev-device-hub/dashboard');
          } else {
            navigate('/ev-admin/dashboard');
          }
        }

        // Operations
        else if (permissions.includes('partnerChargingStationView')) {
          navigate('/ev-admin/operations/charging-station/approved');
        } else if (permissions.includes('partnerChargingDevicesView')) {
          navigate('/ev-admin/operations/chargers');
        } else if (permissions.includes('partnerSessionView')) {
          navigate('/ev-admin/operations/partner-sessions');
        }

        // Revenue
        else if (permissions.includes('partnerTransactionView')) {
          navigate('/ev-admin/revenue/partner-transactions');
        } else if (permissions.includes('partnerPayoutView')) {
          navigate('/ev-admin/revenue/partner-payouts/approved');
        }

        // Configuration
        else if (permissions.includes('partnerTariffView')) {
          navigate('/ev-admin/tariffs');
        }

        // Reports
        else if (permissions.includes('reporting_tools')) {
          navigate('/ev-admin/reports/energyReport');
        }

        // Tools
        else if (permissions.includes('partnerQrCodeView')) {
          navigate('/ev-admin/tools/qr-code-generator');
        } else if (
          permissions.includes('partnerTariffCalculationView') ||
          permissions.includes('partnerTariffCalculation')
        ) {
          navigate('/ev-admin/tools/calculator');
        } else if (permissions.includes('partnerVirtualLoadBalancerView')) {
          navigate('/ev-admin/tools/vlbc');
        } else if (permissions.includes('partnerMaintenanceScheduleView')) {
          navigate('/ev-admin/tools/maintenance-scheduling');
        }

        // Default
        else {
          navigate('/ev-admin/profile');
        }
      } else {
        navigate('/ev-admin/profile');
      }
    } else {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: 401,
            message: t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Handles login submission and opens a centered popup for deactivated tenants.
   * @returns {void}
   */
  const submitFunction = (): void => {
    setSubmitOpen(true);
    setCredentialError(false);
    localStorage.removeItem('activePortal');
    clearSessionAndRedirect();
    if (
      portalType !== PortalType.PARTNER &&
      credentials.countryCode !== '' &&
      credentials.password !== '' &&
      (emailValidation(credentials.emailOrPhoneNumber) ||
        phoneNumberValidation(credentials.emailOrPhoneNumber))
    ) {
      setDisabled(true);
      const request: {
        password: string;
        emailOrPhoneNumber: string;
        countryCode?: string;
        platform: string;
      } = {
        password: credentials.password,
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
        platform: credentials.platform,
      };
      if (phoneNumberValidation(credentials.emailOrPhoneNumber)) {
        request.countryCode = credentials.countryCode;
      }
      authApi
        .login(request)
        .then((response) => {
          const responseData = response?.data;
          setLocalStorageItem('ev-access-token', responseData?.access?.token);
          setLocalStorageItem('ev-access-expires', responseData?.access?.expires);

          // Store tenantId from jwt payload, used for roaming (OCPI) module requests
          const decodedToken = decodeJWT(responseData?.access?.token);
          if (decodedToken?.tenantId) {
            setLocalStorageItem('tenantId', decodedToken.tenantId);
          }
          setLocalStorageItem('internalApiKey', config.internalApiKey);

          // Storing client name
          setLocalStorageItem('client-name', responseData?.clientName.toLowerCase());

          // Store access token inside cookies
          // setCookie(
          //   'ev-access-token',
          //   responseData?.access?.token,
          //   null,
          //   responseData?.access?.expires,
          // );

          // Store refresh token inside cookies
          // setCookie(
          //   'ev-refresh-token',
          //   responseData?.refresh?.token,
          //   null,
          //   responseData?.refresh?.expires,
          // );

          const userVerification = {
            isEmailVerified: responseData?.isEmailVerified,
            isPhoneNumberVerified: responseData?.isPhoneNumberVerified,
          };

          // Set verification in local storage to use in email verification page
          setLocalStorageItem('userVerification', userVerification);
          setDisabled(false);

          const permissions = permissionList();
          const platformPermissions = platformPermissionList();

          if (!platformPermissions || platformPermissions.length === 0) {
            removeLocalStorageItem('ev-access-token');
            removeLocalStorageItem('ev-access-expires');
            removeLocalStorageItem('tenantId');
            removeLocalStorageItem('internalApiKey');
            removeLocalStorageItem('client-name');
            removeLocalStorageItem('userVerification');
            removeLocalStorageItem('partnerName');
            removeLocalStorageItem('accountCustomId');
            dispatch(
              setNotificationMessage({
                message: t('noPermissionsError'),
                status: true,
                type: 'error',
              }),
            );
            return;
          }

          let selectedPortal =
            getLocalStorageItem('activePortal') &&
            platformPermissions.includes(getLocalStorageItem('activePortal'))
              ? getLocalStorageItem('activePortal')
              : '';

          if (!selectedPortal) {
            if (portalType && platformPermissions.includes(portalType)) {
              selectedPortal = portalType;
            } else {
              const priorityOrder = [
                PortalType.NETWORKS,
                PortalType.MOBILITY,
                PortalType.CHARGER,
                PortalType.ROAMING,
                PortalType.PARTNER,
                PortalType.USERS,
              ];
              selectedPortal =
                priorityOrder.find((p) => platformPermissions.includes(p)) ||
                platformPermissions[0] ||
                '';
            }
          }

          setActivePortal(selectedPortal);

          const hasPortalAccess = platformPermissions.includes(PortalType.NETWORKS);

          const isSelectedPortal = selectedPortal === PortalType.NETWORKS;
          if (hasPortalAccess && isSelectedPortal) {
            if (permissions.includes('cpoDashboard')) {
              navigate('/ev-admin/dashboard');
            } else if (permissions.includes('cpoPartnerView')) {
              navigate('/ev-admin/accounts/partners');
            } else if (permissions.includes('cpoUtilityProviderView')) {
              navigate('/ev-admin/accounts/utility-provider');
            } else if (permissions.includes('cpoChargingStationView')) {
              navigate('/ev-admin/operations/charging-station/approved');
            } else if (permissions.includes('cpoChargingPointView')) {
              navigate('/ev-admin/operations/chargers');
            } else if (permissions.includes('cpoSessionView')) {
              navigate('/ev-admin/operations/cpo-sessions');
            } else if (permissions.includes('cpoTransactionView')) {
              navigate('/ev-admin/finance/revenue/cpo-transactions');
            } else if (permissions.includes('cpoRevenueSharingView')) {
              navigate('/ev-admin/finance/revenue/revenue-sharing');
            } else if (permissions.includes('cpoCpoPayoutsView')) {
              navigate('/ev-admin/finance/revenue/cpo-payouts/approved');
            } else if (permissions.includes('cpoRevenueView')) {
              navigate('/ev-admin/finance/configuration/revenue-management');
            } else if (permissions.includes('cpoTariffView')) {
              navigate('/ev-admin/tariffs');
            } else if (permissions.includes('cpoEnergyReportView')) {
              navigate('/ev-admin/reports/energy-report');
            } else if (permissions.includes('cpoQrCodeGenerationView')) {
              navigate('/ev-admin/tools/qr-code-generator');
            } else if (permissions.includes('cpoCampaignManagementView')) {
              navigate('/ev-admin/tools/campaign');
            } else if (permissions.includes('cpoTariffCalculation')) {
              navigate('/ev-admin/tools/calculator');
            } else {
              navigate('/login/credentials');
            }
          } else if (
            platformPermissions.includes(PortalType.MOBILITY) &&
            selectedPortal === PortalType.MOBILITY
          ) {
            if (permissions.includes('mobilityEndConsumerView')) {
              navigate('/ev-admin/end-consumer');
            } else if (permissions.includes('mobilityChargersView')) {
              navigate('/ev-admin/operations/personal-charger');
            } else if (permissions.includes('mobilitySessionsView')) {
              navigate('/ev-admin/operations/emsp-sessions');
            } else if (permissions.includes('mobilityGuestUserView')) {
              navigate('/ev-admin/operations/guest-charging');
            } else if (permissions.includes('mobilityTransactionsView')) {
              navigate('/ev-admin/revenue/emsp-transactions');
            } else if (permissions.includes('mobilityPayoutsView')) {
              navigate('/ev-admin/revenue/emsp-payouts/approved');
            } else if (permissions.includes('mobilityFeeConfigurationView')) {
              navigate('/ev-admin/configuration/fee-configuration');
            } else if (permissions.includes('mobilityPaymentGatewayView')) {
              navigate('/ev-admin/configuration/paymentGateway');
            } else if (permissions.includes('mobilityEnergyReportView')) {
              navigate('/ev-admin/reports/energyReport');
            } else if (permissions.includes('mobilityRevenueReportView')) {
              navigate('/ev-admin/reports/revenueReport');
            } else {
              navigate('/login/credentials');
            }
          } else if (
            platformPermissions.includes(PortalType.CHARGER) &&
            selectedPortal === PortalType.CHARGER
          ) {
            if (permissions.includes('devicesDashboard')) {
              navigate('/ev-admin/ev-device-hub/dashboard');
            } else if (permissions.includes('devicesEvDeviceHubView')) {
              navigate('/ev-admin/ev-device-hub/devices');
            } else if (permissions.includes('devicesRatingView')) {
              navigate('/ev-admin/connector');
            } else if (permissions.includes('devicesBrandsView')) {
              navigate('/ev-admin/brand');
            } else if (permissions.includes('devicesModelsView')) {
              navigate('/ev-admin/model');
            } else {
              navigate('/login/credentials');
            }
          } else if (
            platformPermissions.includes(PortalType.ROAMING) &&
            selectedPortal === PortalType.ROAMING
          ) {
            navigate('/ev-admin/ocpi');
          } else if (
            platformPermissions.includes(PortalType.PARTNER) &&
            selectedPortal === PortalType.PARTNER
          ) {
            navigate('/ev-admin/partner');
          } else if (
            platformPermissions.includes(PortalType.USERS) &&
            selectedPortal === PortalType.USERS
          ) {
            if (permissions.includes('userManagementView')) {
              navigate('/ev-admin/user-management/user');
            } else if (permissions.includes('userRoleManagementView')) {
              navigate('/ev-admin/user-management/role');
            } else {
              navigate('/login/credentials');
            }
          } else {
            navigate('/login/credentials');
          }
        })
        .catch((error) => {
          setDisabled(false);
          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'No internet connection. Please check your network and try again.',
                status: true,
                type: 'error',
              }),
            );
            return;
          }
          if (error?.response?.data?.code === 423) {
            setTenantDeactivatedPopupOpen(true);
            setDisabled(false);
            return;
          }
          setCredentialError(true);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
    if (
      portalType === PortalType.PARTNER &&
      credentials.type !== '' &&
      credentials.countryCode !== '' &&
      credentials.password !== '' &&
      textAndNumberValidation(20, 20, credentials.accountCustomId || '') &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
    ) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: true,
      });
      const request = {
        countryCode: credentials.countryCode,
        accountCustomId: credentials.accountCustomId,
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
        password: credentials.password,
      };
      authApi
        .login(request)
        .then((response) => {
          setCookie('accountCustomId', credentials.accountCustomId || '', null, null);
          loginSuccessHandler(response?.data);

          // Storing client name
          setLocalStorageItem('client-name', response?.data?.clientName.toLowerCase());
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          setCredentialError(true);

          if (!error.response) {
            dispatch(
              setNotificationMessage({
                message: 'Oops... No internet or network error',
                status: true,
                type: 'error',
              }),
            );
            return;
          }
          
          if (error?.response?.data?.code === 423) {
            setTenantDeactivatedPopupOpen(true);
            setDisabled(false);
            return;
          }

          dispatch(
            setNotificationMessage({
              type: 'error',
              notificationDetails: {
                statusCode: 200, // Code is not required, if we pass code as 404 means then page will redirect to login (Just as refresh)
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    }
  };
  useEffect(() => {
    // Access specific query parameters
    const accessToken = searchParameters.get('accessToken');
    const accountVerificationStatus = searchParameters.get('accountVerificationStatus');
    const accessTokenExpires = searchParameters.get('accessTokenExpires');
    const isEmailVerified = searchParameters.get('isEmailVerified') === 'true';
    const isPhoneNumberVerified = searchParameters.get('isPhoneNumberVerified') === 'true';
    const isInitialPasswordReset = searchParameters.get('isInitialPasswordReset') === 'true';
    const clientName = searchParameters.get('clientName');
    const targetLanguage = searchParameters.get('lng');
    const partnerName = searchParameters.get('partnerName');
    const queryAccountCustomId = searchParameters.get('accountCustomId');

    if (partnerName) {
      setLocalStorageItem('partnerName', partnerName);
    }

    if (queryAccountCustomId) {
      setLocalStorageItem('accountCustomId', queryAccountCustomId);
    }

    if (targetLanguage && ['en', 'ar', 'fr', 'sp'].includes(targetLanguage)) {
      i18n.changeLanguage(targetLanguage);
      setCookie('i18next', targetLanguage, null, null);
      setLocalStorageItem('i18nextLng', targetLanguage);
    }

    if (clientName) {
      setLocalStorageItem('client-name', clientName.toLowerCase());
    }

    const effectiveClientName = clientName || getLocalStorageItem('client-name');

    if (accessToken && accountVerificationStatus && accessTokenExpires) {
      loginSuccessHandler({
        accountVerificationStatus,
        access: {
          token: accessToken,
          expires: accessTokenExpires,
        },
        clientName: effectiveClientName,
        // refresh: {
        //   token: refreshToken,
        //   expires: refreshTokenExpires,
        // },
        isEmailVerified,
        isPhoneNumberVerified,
        isInitialPasswordReset,
      });
    }
  }, [location.search, searchParameters]);

  // useEffect(() => {
  //   if (portalType === PortalType.PARTNER) {
  //     // dispatch(resetAllSlices());
  //     const hasUrlToken = searchParameters.has('accessToken');
  //     if (!hasUrlToken) {
  //       const accessToken = getCookie('ev-access-token') || getLocalStorageItem('ev-access-token');
  //       if (accessToken) {
  //         navigate('../ev-admin/dashboard');
  //       }
  //     }
  //   }
  // }, []);

  return (
    <div className="login-component-v2" dir={i18n.dir()}>
      <ModalBox
        open={tenantDeactivatedPopupOpen}
        title={null}
        content={
          <div className="conform-box-body tenant-deactivated-popup" role="alert">
            <AlertIcon aria-hidden="true" className="tenant-deactivated-popup-icon" />
            <p className="sub-title tenant-deactivated-popup-message">{t('tenantDeactivated')}</p>
          </div>
        }
        closeModal={() => setTenantDeactivatedPopupOpen(false)}
        header={false}
      />
      <div className="login-component-v2-header">
        <p className="title">{t('signIn')}</p>
      </div>
      <div className="login-component-v2-body">
        {portalType === PortalType.PARTNER && (
          <CustomInput
            inputLabel={t('accountId')}
            type="text"
            placeholder={t('enterAccountId')}
            name="accountCustomId"
            value={credentials.accountCustomId || ''}
            onChangeValue={handleCredentialChange}
            error={hasAccountIdError()}
            errorMessage={accountIdErrorMessage()}
          />
        )}
        <div className="email-or-phone-number">
          <RadioButton
            name="type"
            value={credentials.type}
            list={[
              {
                label: `${t('email')}`,
                value: 'email',
              },
              {
                label: `${t('phoneNumber')}`,
                value: 'phone',
              },
            ]}
            onChangeValue={handleCredentialChange}
          />
          {credentials.type === 'email' ? (
            <CustomInput
              type="email"
              placeholder={t('enterEmail')}
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              onChangeValue={handleCredentialChange}
              error={hasEmailOrPhoneError()}
              errorMessage={emailOrPhoneErrorMessage()}
            />
          ) : (
            <CustomPhoneInput
              type="number"
              placeholder={t('enterPhoneNumber')}
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              value1={credentials.countryCode}
              onChangeValue={handleCredentialChange}
              error={hasEmailOrPhoneError()}
              errorMessage={emailOrPhoneErrorMessage()}
              minLength={5}
              maxLength={13}
            />
          )}
        </div>
        <div className="password-container">
          <CustomInput
            inputLabel={t('password')}
            type="password"
            placeholder={t('enterPassword')}
            name="password"
            value={credentials.password}
            onChangeValue={handleCredentialChange}
            error={hasPasswordError()}
            errorMessage={passwordErrorMessage()}
          />
        </div>
        <div className="forgot-password">
          <button type="button" onClick={() => navigate('../login/forgot-password')}>
            {t('forgotPassword')}
          </button>
        </div>
        <MyButton
          label={t('signIn')}
          buttonType="submit"
          onClickFunc={submitFunction}
          disableButton={disabled || formStatus.buttonLoader}
          loading={disabled || formStatus.buttonLoader}
        />
      </div>
      <div className="language-selector">
        <LanguageSelector isCollapsed={false} />
      </div>
    </div>
  );
}

export default LoginComponent;
